/**
 * Path Parser
 *
 * Universal parser for entity paths in format:
 * @entityType[entityID].relation[relationID].field[index]
 *
 * Examples:
 * - @audience[abc123]
 * - @audience[abc123].name
 * - @audience[abc123].actors[actor1]
 * - @audience[abc123].actors[actor1].fears[0]
 * - @task[xyz789]
 * - @task[xyz789].description
 * - @task[xyz789].funnelGoals[0]
 * - @variable[age]
 * - @insightVariable[weight_loss_trigger]
 */

/**
 * Entity types supported by the parser
 */
export type EntityType = 'audience' | 'task' | 'variable' | 'insightVariable' | 'review' | 'rating' | 'studyCase' | 'bigNumber' | 'insightsAnalysis' | 'unknown';

/**
 * Path types based on what the path points to
 */
export type PathType = 'entity' | 'relation' | 'field' | 'arrayItem' | 'subcollection';

/**
 * Parsed path structure
 */
export interface ParsedPath {
  /** Type of entity (audience, task, etc.) */
  entityType: EntityType;

  /** Entity ID */
  entityId: string;

  /** Relation ID (for nested entities like actors) */
  relationId?: string;

  /** Field name */
  fieldName?: string;

  /** Array index (for array items) */
  arrayIndex?: number;

  /** Subcollection name (for subcollections) */
  subcollectionName?: string;

  /**
   * W13 (DM1): the `originalItemId` of a quoted actor source, set when an
   * insight path carries the evidence sub-path `.sources[<originalItemId>]`.
   * The path still addresses the THESIS (`relationId`); this is detail.
   */
  sourceItemId?: string;

  /** What type of path this is */
  type: PathType;

  /** Original path string */
  originalPath: string;
}

/**
 * Detects entity type from path prefix
 *
 * @param path - Path string
 * @returns Entity type
 */
export function detectEntityType(path: string): EntityType {
  if (path.startsWith('@audience[')) return 'audience';
  if (path.startsWith('@task[')) return 'task';
  // Insight-derived variables (Task 9): a SEPARATE plane from project
  // variables — checked before '@variable[' so the prefixes never collide.
  if (path.startsWith('@insightVariable[')) return 'insightVariable';
  if (path.startsWith('@variable[')) return 'variable';
  if (path.startsWith('@review[')) return 'review';
  if (path.startsWith('@rating[')) return 'rating';
  if (path.startsWith('@studyCase[')) return 'studyCase';
  if (path.startsWith('@bigNumber[')) return 'bigNumber';
  if (path.startsWith('@insight[')) return 'insightsAnalysis';
  return 'unknown';
}

/**
 * Parses a path string into structured components
 *
 * @param path - Path string to parse
 * @returns Parsed path object or null if invalid
 */
export function parsePath(path: string): ParsedPath | null {
  const entityType = detectEntityType(path);

  if (entityType === 'unknown') {
    return null;
  }

  // Whole insight analysis: @insight[analysisId]
  // Must be checked BEFORE the generic entityMatch below: the "@insight" path
  // tag doesn't match the canonical EntityType ('insightsAnalysis'), so the
  // generic regex would otherwise capture entityType: 'insight' (a value not
  // in the EntityType union) and downstream consumers (routePaths' switch)
  // would drop the path as an unsupported entity type. See defect D3.
  if (entityType === 'insightsAnalysis') {
    const wholeMatch = path.match(/^@insight\[([^\]]+)\]$/);
    if (wholeMatch) {
      return {
        entityType: 'insightsAnalysis',
        entityId: wholeMatch[1],
        type: 'entity',
        originalPath: path,
      };
    }
  }

  // Whole entity: @audience[ID] or @task[ID]
  const entityMatch = path.match(/^@(\w+)\[([^\]]+)\]$/);
  if (entityMatch) {
    return {
      entityType: entityMatch[1] as EntityType,
      entityId: entityMatch[2],
      type: 'entity',
      originalPath: path,
    };
  }

  // For audience paths with actors (relation)
  if (entityType === 'audience') {
    // Whole actor: @audience[ID].actors[actorID]
    const actorMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]$/);
    if (actorMatch) {
      return {
        entityType: 'audience',
        entityId: actorMatch[1],
        relationId: actorMatch[2],
        type: 'relation',
        originalPath: path,
      };
    }

    // Array item with actor: @audience[ID].actors[actorID].field[indexOrFirebaseId]
    // Supports both numeric index (fears[0]) and Firebase ID (fears[NY2dMsB0...])
    // Also strips trailing .description or other field suffixes
    const actorArrayItemMatch = path.match(
      /^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([^[]+)\[([^\]]+)\](?:\.\w+)?$/
    );
    if (actorArrayItemMatch) {
      const indexOrId = actorArrayItemMatch[4];
      const isNumeric = /^\d+$/.test(indexOrId);
      return {
        entityType: 'audience',
        entityId: actorArrayItemMatch[1],
        relationId: actorArrayItemMatch[2],
        fieldName: actorArrayItemMatch[3],
        arrayIndex: isNumeric ? parseInt(indexOrId, 10) : undefined,
        type: 'arrayItem',
        originalPath: path,
      };
    }

    // Subcollection or field with actor: @audience[ID].actors[actorID].field
    const actorFieldMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([^.[]+)$/);
    if (actorFieldMatch) {
      const fieldName = actorFieldMatch[3];
      const knownSubcollections = [
        'coreJobs',
        'contexts',
        'fears',
        'bigJobs',
        'subtasks',
        'competitors',
        'habits',
      ];

      return {
        entityType: 'audience',
        entityId: actorFieldMatch[1],
        relationId: actorFieldMatch[2],
        fieldName,
        subcollectionName: knownSubcollections.includes(fieldName) ? fieldName : undefined,
        type: knownSubcollections.includes(fieldName) ? 'subcollection' : 'field',
        originalPath: path,
      };
    }

    // Audience field: @audience[ID].field
    const audienceFieldMatch = path.match(/^@audience\[([^\]]+)\]\.([^[]+)$/);
    if (audienceFieldMatch) {
      return {
        entityType: 'audience',
        entityId: audienceFieldMatch[1],
        fieldName: audienceFieldMatch[2],
        type: 'field',
        originalPath: path,
      };
    }
  }

  // For task paths
  if (entityType === 'task') {
    // Array item: @task[ID].field[index] with optional .suffix
    const taskArrayItemMatch = path.match(/^@task\[([^\]]+)\]\.([^[]+)\[(\d+)\](?:\.\w+)?$/);
    if (taskArrayItemMatch) {
      return {
        entityType: 'task',
        entityId: taskArrayItemMatch[1],
        fieldName: taskArrayItemMatch[2],
        arrayIndex: parseInt(taskArrayItemMatch[3], 10),
        type: 'arrayItem',
        originalPath: path,
      };
    }

    // Field: @task[ID].field
    const taskFieldMatch = path.match(/^@task\[([^\]]+)\]\.([^[]+)$/);
    if (taskFieldMatch) {
      const fieldName = taskFieldMatch[2];
      const knownArrayFields = [
        'funnelGoals',
        'requiredInfo',
        'competitorsForClients',
        'userCondition',
      ];

      return {
        entityType: 'task',
        entityId: taskFieldMatch[1],
        fieldName,
        subcollectionName: knownArrayFields.includes(fieldName) ? fieldName : undefined,
        type: knownArrayFields.includes(fieldName) ? 'subcollection' : 'field',
        originalPath: path,
      };
    }
  }

  // For social proof entities (review, rating, studyCase, bigNumber) — flat entities
  const socialProofTypes: EntityType[] = ['review', 'rating', 'studyCase', 'bigNumber'];
  if (socialProofTypes.includes(entityType)) {
    // Field: @review[ID].field
    const fieldMatch = path.match(/^@(\w+)\[([^\]]+)\]\.([^[]+)$/);
    if (fieldMatch) {
      return {
        entityType,
        entityId: fieldMatch[2],
        fieldName: fieldMatch[3],
        type: 'field',
        originalPath: path,
      };
    }
  }

  // For insights analysis — hierarchical: analysis → category → individual insight
  if (entityType === 'insightsAnalysis') {
    // Single insight item: @insight[analysisId].category[insightId], optionally
    // followed by the W13 evidence sub-path .sources[originalItemId] — one actor
    // quote of that same thesis (rulings W13-R4 / W13-R17). The suffix names a
    // VARIANT, so the parsed path still addresses the thesis (`relationId`) and
    // the item id rides along in `sourceItemId`.
    //
    // MIRROR (ruling W13-R28) — this regex is BYTE-IDENTICAL to
    // `strategy-blocks/utils/insightCitationPath.ts` (INSIGHT_CITATION) and
    // `builder-service/services/cli/insightImpact.ts` (GRAMMAR_1). The three
    // cannot import one another (builder-service deploy Guard 4 forbids a value
    // import across the boundary), so they are kept in sync by hand and each
    // carries the SAME malformed-case table in its own test file. Keep all three
    // in step, or none of them is trustworthy.
    //
    // Every class is closed on purpose: the category excludes `.` as well as
    // brackets, so `@insight[a].fears.sources[item-9]` — a suffix hung on the
    // CATEGORY, with no thesis — cannot parse as category `fears.sources` with
    // the item key promoted to a phantom thesis id. The optional group demands a
    // non-empty key and the pattern is `$`-anchored, so every other malformed
    // suffix parses as null too (AC-W13-33).
    const insightItemMatch = path.match(/^@insight\[([^\]]+)\]\.([^[\].]+)\[([^\]]+)\](?:\.sources\[([^\]]+)\])?$/);
    if (insightItemMatch) {
      return {
        entityType: 'insightsAnalysis',
        entityId: insightItemMatch[1],
        subcollectionName: insightItemMatch[2],
        relationId: insightItemMatch[3],
        ...(insightItemMatch[4] ? { sourceItemId: insightItemMatch[4] } : {}),
        type: 'arrayItem',
        originalPath: path,
      };
    }

    // Whole category: @insight[analysisId].category
    const categoryMatch = path.match(/^@insight\[([^\]]+)\]\.([^[.]+)$/);
    if (categoryMatch) {
      return {
        entityType: 'insightsAnalysis',
        entityId: categoryMatch[1],
        subcollectionName: categoryMatch[2],
        type: 'subcollection',
        originalPath: path,
      };
    }
  }

  return null;
}

/**
 * Builds a path from components
 *
 * @param entityType - Type of entity
 * @param entityId - Entity ID
 * @param options - Additional path components
 * @returns Path string
 */
export function buildPath(
  entityType: EntityType,
  entityId: string,
  options?: {
    relationId?: string;
    fieldName?: string;
    arrayIndex?: number;
  }
): string {
  let path = `@${entityType}[${entityId}]`;

  if (options?.relationId) {
    path += `.actors[${options.relationId}]`;
  }

  if (options?.fieldName) {
    path += `.${options.fieldName}`;
  }

  if (options?.arrayIndex !== undefined) {
    path += `[${options.arrayIndex}]`;
  }

  return path;
}

/**
 * Validates path format
 *
 * @param path - Path string to validate
 * @returns Validation result
 */
export function validatePathFormat(path: string): {
  valid: boolean;
  reason?: string;
} {
  if (!path || typeof path !== 'string') {
    return { valid: false, reason: 'Path must be a non-empty string' };
  }

  if (!path.startsWith('@')) {
    return { valid: false, reason: 'Path must start with @' };
  }

  const parsed = parsePath(path);
  if (!parsed) {
    return { valid: false, reason: 'Invalid path format' };
  }

  if (parsed.entityType === 'unknown') {
    return { valid: false, reason: `Unknown entity type in path: ${path}` };
  }

  return { valid: true };
}
